import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import LangSwitcher from '@/components/LangSwitcher';

const TEXTURE = '/garde-valley.jpg';

export default function ResetPassword() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const redirectTo = `${window.location.origin}/auth/reset/confirm`;
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
                { email: email.trim(), redirect_to: redirectTo });
            setSent(true);
        } catch {
            // Réponse générique : ne jamais révéler si le compte existe
            setSent(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="grid lg:grid-cols-2 min-h-screen">
                {/* Left — visual */}
                <div className="hidden lg:block relative">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url("${TEXTURE}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                    <div
                        className="absolute inset-x-0 top-0 h-24 pointer-events-none"
                        style={{ backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)' }}
                    />
                    <div className="relative h-full flex flex-col p-10">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-background/85 hover:text-background text-sm transition-colors w-fit"
                        >
                            ← Retour à l'accueil
                        </Link>
                    </div>
                </div>

                {/* Right — form */}
                <div className="flex items-center justify-center p-6 lg:p-12">
                    <div className="w-full max-w-md">
                        <div className="flex justify-end mb-4">
                            <LangSwitcher />
                        </div>
                        <Link
                            to="/auth/signin"
                            className="inline-flex items-center gap-1.5 text-sm text-foreground/65 hover:text-primary mb-8"
                            data-testid="link-back-to-signin"
                        >
                            <ArrowLeft size={14} /> Retour à la connexion
                        </Link>

                        {!sent ? (
                            <>
                                <h1 className="font-heading text-3xl mb-3">Mot de passe oublié&nbsp;?</h1>
                                <p className="text-foreground/70 leading-[1.7] mb-8">
                                    Pas d'inquiétude. Saisissez votre adresse e-mail —
                                    nous vous envoyons un lien sécurisé pour définir un nouveau mot de passe.
                                </p>

                                <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Adresse e-mail</Label>
                                        <Input
                                            id="email" type="email" required
                                            value={email} onChange={(e) => setEmail(e.target.value)}
                                            className="h-12" placeholder="vous@organisation.com"
                                            data-testid="reset-email"
                                        />
                                    </div>
                                    {error && (
                                        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                                            {error}
                                        </div>
                                    )}
                                    <Button
                                        type="submit" disabled={loading}
                                        className="w-full h-12 bg-primary hover:bg-primary-hover text-primary-foreground"
                                        data-testid="reset-submit"
                                    >
                                        {loading ? <Loader2 className="animate-spin" size={16} /> : 'Recevoir le lien sécurisé'}
                                    </Button>
                                </form>
                            </>
                        ) : (
                            <div className="space-y-6" data-testid="reset-success">
                                <div className="h-12 w-12 rounded-full bg-secondary-light text-secondary flex items-center justify-center">
                                    <MailCheck size={22} />
                                </div>
                                <h1 className="font-heading text-3xl">Lien envoyé.</h1>
                                <p className="text-foreground/75 leading-[1.7]">
                                    Si un compte existe à l'adresse <strong>{email}</strong>,
                                    vous recevrez un e-mail dans les prochaines minutes avec un lien
                                    sécurisé pour réinitialiser votre mot de passe.
                                </p>
                                <p className="text-sm text-foreground/60 leading-[1.7]">
                                    Pensez à vérifier votre dossier spam si vous ne voyez rien arriver.
                                    Le lien est valable 1 heure.
                                </p>
                                <Link to="/auth/signin">
                                    <Button variant="outline" className="w-full h-12">
                                        Retour à la connexion
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
